import { useState, useEffect, useContext, Dispatch, SetStateAction, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  Switch,
  SelectChangeEvent,
  Typography,
  Divider,
  Autocomplete,
  TextField,
} from "@mui/material";
import ButtonArea from "../common/ButtonArea";
import { DeviceTable, PageState } from "../common/table/DeviceTable";
import { FormLabel } from "../common/form/FormLabel";
import { FormText } from "../common/form/FormText";
import { PullDownItem, FormPullDown } from "../common/form/FormPullDown";
import { DeviceSearchRequest, DeviceSearchResponse } from "./api/TerminalForm";
import { TerminalService } from "./api/TerminalService";
import { Utility } from "../../utils/Utility";
import Constants from "../../utils/Constants";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { Loading } from "../common/Loading";
import { UserContext } from "../../UserProvider";
import { useCookies } from "react-cookie";
import { TenantService } from "../tenant/api/TenantService";
import { TenantNameRequest, TenantNameResponse } from "../tenant/api/TenantForm";
import { AxiosResponse } from "axios";

const TerminalList = () => {
  const navigate = useNavigate();
  const PAGE_NAME = "端末一覧";

  /***************************************************************
   * State管理
   **************************************************************/
  // 共通ローディング
  const [isLoading, setIsLoading] = useState(false);
  // 検索条件
  const cache = useContext(UserContext) as {
    initialized: boolean,
    isFlushView: boolean,
    value: {
      tagOption: PullDownItem[],
      tagValue: PullDownItem[],
      tenantOption: PullDownItem[],
      tenantValue: PullDownItem[],
      request: DeviceSearchRequest,
      lastCondition?: DeviceSearchRequest,
      deviceList: DeviceSearchResponse[],
      pageState: PageState,
    },
  };
  const [request, setRequest] = useState<DeviceSearchRequest>({});
  // 端末一覧
  const [deviceList, setDeviceList] = useState([] as DeviceSearchResponse[]);
  // タグ選択肢
  const [tagOption, setTagOption] = useState<PullDownItem[]>([]);
  // テナント選択肢
  const [tenantOption, setTenantOption] = useState<PullDownItem[]>([]);
  // 選択されたテナント
  const [selectedTenant, setSelectedTenant] = useState<PullDownItem | null>(null);
  const [pageState, setPageState] = useState<PageState>({
    checked: {},
    page: 0,
    rowsPerPage: 15,
    order: "asc",
    orderBy: "imei",
  });

  // 共通ダイアログ(ステータス)
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  // 権限リスト
  const [{ authList }] = useCookies(["authList"]);
  const authSet = {
    'ALL_NONE_SUB-TENANT': false,
  };
  authList.forEach((auth: keyof typeof authSet) => { authSet[auth] = true });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    if (!cache.initialized) {
      // テナント選択肢取得
      getTenantNameList();
      // 検索処理
      // searchButtonClick();
      cache.initialized = true;
      cache.value = { tagOption, tagValue: [], tenantOption, tenantValue: [], request, deviceList, pageState };
    } else if (cache.isFlushView && cache.value.lastCondition) {
      // 前回検索した条件が残っており、かつデータが更新された場合、前回と同じ条件で再検索
      cache.isFlushView = false;
      getTenantNameList();
      setTagOption(cache.value.tagOption);
      setTenantOption(cache.value.tenantOption);
      setRequest(cache.value.request);
      // setDeviceList は、search() 内で呼び出される
      setDeviceList(cache.value.deviceList);
      setPageState(cache.value.pageState);
      search(true);
    } else {
      getTenantNameList();
      setTagOption(cache.value.tagOption);
      setTenantOption(cache.value.tenantOption);
      setRequest(cache.value.request);
      setDeviceList(cache.value.deviceList);
      setPageState(cache.value.pageState);
    }
  }, []);

  // プルダウン：ロック中端末
  const lockOption: PullDownItem[] = [
    {
      key: "button_lock",
      value: "ボタン操作ロック中",
    },
    {
      key: "webui_lock",
      value: "WebUIロック中",
    },
    {
      key: "conn_lock",
      value: "通信ロック中",
    },
  ];

  // プルダウン：機種のチェンジイベント
  const modelOption: PullDownItem[] = [
    {
      key: "FS050W",
      value: "FS050W",
    },
  ];

  // プルダウン：オンライン
  const onlineOption: PullDownItem[] = [
    {
      key: "false",
      value: "オフライン",
    },
    {
      key: "true",
      value: "オンライン",
    },
  ];

  // テナント名一覧取得API
  const getTenantNameList = () => {
    const service = new TenantService();
    service
      .findTenantNameList(new TenantNameRequest)
      .then((response: AxiosResponse) => {
        const tenantNameList: TenantNameResponse[] = response.data.data;
        const tenantOptions = Utility.getTenantNameOption(tenantNameList);
        setTenantOption(tenantOptions);
        
        // キャッシュから選択されたテナントを復元
        if (cache.value.request?.tenantIds && cache.value.request.tenantIds.length > 0) {
          const selectedTenantId = cache.value.request.tenantIds[0];
          const selectedTenantItem = tenantOptions.find(option => option.key === selectedTenantId);
          setSelectedTenant(selectedTenantItem || null);
        }
      })
      .catch((err) => {})
      .finally(() => {});
  };

  const label = { inputProps: { "aria-label": "Switch demo" } };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent
  ) => {
    setRequest(cache.value.request = { ...request, [e.target.name]: e.target.value });
  };

  const handleImeiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRequest(cache.value.request = { ...request, [e.target.name]: e.target.value });
  };
  const handleTenantAutocompleteChange = (e: React.SyntheticEvent, value: PullDownItem | null) => {
    setSelectedTenant(value);
    if (value !== null) {
      const tenantKey = value.key;
      setRequest(cache.value.request = { ...request, tenantIds: [tenantKey] });
    } else {
      setRequest(cache.value.request = { ...request, tenantIds: [] });
    }
  }
  // 一覧のページ番号・ソート順変更を検知
  const handleChangePageState = useCallback((_pageState: PageState) => {
    setPageState(cache.value.pageState = _pageState);
  }, []);

  // 検索実行(Enter)
  const handleInputSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchButtonClick();
    }
  };
  const SearchForm = () => {
    return (
      <Box sx={{ p: 2 }}>
        {/* 検索条件欄 */}
        <Grid container spacing={2} alignItems="center">

          {/* IMEI検索欄 */}
          <Grid item xs={2}>
            <TextField
              name="imei"
              type="text"
              label="IMEI / シリアルナンバー"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={request.imei || ""}
              onChange={handleImeiChange}
              onKeyDown={handleInputSearch}
            />
          </Grid>

          {/* テナント検索欄 */}
          {/* {authSet["ALL_NONE_SUB-TENANT"] && <> */}
            <Grid item xs={4}>
              <Autocomplete
                key={selectedTenant?.key || 'tenant-autocomplete'}
                size="small"
                fullWidth
                // multiple
                options={tenantOption ?? []}
                getOptionLabel={(option) => option.value}
                isOptionEqualToValue={(option, v) =>
                  option.key == v.key
                }
                value={selectedTenant}
                onChange={(e, value) => handleTenantAutocompleteChange(e, value)}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      name="tenantIds"
                      label="テナント"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )
                }}
              />
            </Grid>

          {/* 検索ボタン */}
          <Grid item xs={1}>
            <Button variant="contained" onClick={searchButtonClick}>
              検索
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const addButtonClick = () => {
    navigate("/terminal_import");
  };

  // const exportButtonClick = () => {
  //   setIsLoading(true);
  //   // 端末情報エクスポートAPI
  //   const service = new TerminalService();
  //   service
  //     .export(cache.value.lastCondition ?? request)
  //     .then((response) => {
  //       // CSV出力
  //       Utility.exportCsv(response);
  //     })
  //     .catch(async (err: any) => {
  //       if (err.response.data?.type === "application/json") {
  //         const response = { data: JSON.parse(await err.response.data.text()) };
  //         if (Constants.ERR_STATUS_NG === response.data.status) {
  //           setIsDialogOpen(true);
  //           setResultResponse(response.data);
  //           return;
  //         }
  //       }
  //       setStateResultResponse(
  //         Constants.ERR_STATUS_NG,
  //         err.response.status,
  //         err.response.statusText,
  //         Constants.E_MSG_SYSTEM_ERR
  //       );
  //     })
  //     .finally(() => {
  //       setIsLoading(false);
  //     });
  // };

  const editButtonClick = () => {
    sessionStorage.setItem("DEVICE_ID", selectedIdList().toString());
    navigate("/terminal_edit");
  };

  // const settingButtonClick = () => {
  //   sessionStorage.setItem("DEVICE_ID", selectedIdList().toString());
  //   navigate("/terminal_setting");
  // };

  // const operationButtonClick = () => {
  //   sessionStorage.setItem("DEVICE_ID", selectedIdList().toString());
  //   navigate("/terminal_operation");
  // };

  // 検索ボタン押下 - 端末一覧取得API
  const searchButtonClick = () => {
    search();
  }
  // 検索処理
  // isReload: true = 前回の条件で再検索
  const search = (isReload: boolean = false) => {
    setIsLoading(true);
    const service = new TerminalService();
    service
      .findByConditions((isReload && cache.value.lastCondition) ? cache.value.lastCondition : request)
      .then((response) => {
        if (Constants.ERR_STATUS_OK === response.data.status) {
          // OK
          cache.value.lastCondition = request;
          setDeviceList(cache.value.deviceList = response.data.data);
          if (!isReload || cache.value.pageState.rowsPerPage === 0) {
            // 検索実行した場合は 1ページ目を表示。検索条件を引き継いでリストのリロードを実施した場合は、前回表示していたページを表示
            setPageState(cache.value.pageState = {...cache.value.pageState, page: 0});
          } else if (cache.value.pageState.page >= cache.value.deviceList.length / cache.value.pageState.rowsPerPage) {
            // 前回表示していたページが無くなった場合、終端ページを表示する
            const nextPage = Math.floor((cache.value.deviceList.length - 1) / cache.value.pageState.rowsPerPage);
            setPageState(cache.value.pageState = {...cache.value.pageState, page: nextPage});
          }
        } else {
          setIsDialogOpen(true);
          setResultResponse(response.data);
        }
      })
      .catch((err) => {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          err.response.status,
          err.response.statusText,
          Constants.E_MSG_SYSTEM_ERR
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const sliceOnePage = useCallback((
    deviceList: DeviceSearchResponse[],
    pageState: PageState
  ) => {
    const { page, rowsPerPage } = pageState;
    return deviceList.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, []);
  const bulkDisabled = () => {
    // チェックボックスに2つ以上チェックが入っていない場合は非活性
    return Object.keys(pageState.checked).length < 2;
  };
  // チェックが入っている端末の一覧を取得
  const selectedIdList = () => {
    return deviceList.filter((d) => pageState.checked[d.id]).map((d) => d.id);
  };

  return (
    <>
      <Box
        display={"flex"}
        flexDirection={"column"}
        style={{ height: "calc(100vh - 64px)", width: "calc(100vw - 200px)" }}
      >
        <ButtonArea pageName={PAGE_NAME}>
          <Box mr={2}>
            <Button variant="contained" onClick={addButtonClick} >
              新規追加
            </Button>
          </Box>
          {/* <Button variant="contained" onClick={exportButtonClick}>
            エクスポート
          </Button> */}
          <Button
            variant="contained"
            onClick={editButtonClick}
            disabled={bulkDisabled()}
          >
            一括編集
          </Button>
          {/* <Button
            variant="contained"
            onClick={settingButtonClick}
            disabled={bulkDisabled()}
          >
            一括設定
          </Button>
          <Button
            variant="contained"
            onClick={operationButtonClick}
            disabled={bulkDisabled()}
          >
            一括操作
          </Button> */}
        </ButtonArea>
        <Box sx={{ pt: 4 }} flex={"1"} style={{ overflowY: "scroll" }}>
          {/* 検索条件の配置 */}
          {SearchForm()}

          <Divider sx={{ mx: 2 }} />

          {/* 端末一覧表の配置 */}
          <DeviceTable
            deviceList={deviceList}
            {...{ pageState, setPageState: handleChangePageState, sliceOnePage }}
          />
        </Box>
      </Box>

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />

      {/* 処理ローディング */}
      <Loading isLoading={isLoading} />
    </>
  );
};

export default TerminalList;
